// useRequirePremium — reusable Premium route guard.
//
// Centralizes the "is this Premium-only surface allowed for the current user?"
// decision so the isLoading discipline (where the false-positive risk lives) is
// written ONCE, not copy-pasted per route. Same source of truth as the
// MirrorHome pill + the #235 inline guard: useSubscription().
//
// CRITICAL: never block while the subscription is still loading — a redirect
// during load would kick a legit Premium user (the "no flash / no false
// positive" rule from #232 / #235). `redirect` is a <Navigate replace> ONLY
// when access is RESOLVED-and-denied (isLoading === false && isPremium === false);
// it is null while loading OR when the user has access.
//
// Usage (the caller returns `redirect` AFTER all of its own hooks, so the hook
// order stays stable across renders):
//   const { redirect } = useRequirePremium();
//   ...the component's other hooks...
//   if (redirect) return redirect;

import { Navigate } from "react-router-dom";
import type { ReactNode } from "react";
import { useSubscription } from "@/hooks/useSubscription";

export interface UseRequirePremiumResult {
  isPremium: boolean;
  isLoading: boolean;
  // <Navigate to={redirectTo} replace/> when resolved-and-denied; null while
  // loading OR when the user has access. Return it to enforce the gate.
  redirect: ReactNode;
}

export function useRequirePremium(
  opts?: { redirectTo?: string },
): UseRequirePremiumResult {
  const redirectTo = opts?.redirectTo ?? "/pricing";
  const { isPremium, isLoading } = useSubscription();
  const redirect: ReactNode =
    !isLoading && !isPremium ? <Navigate to={redirectTo} replace /> : null;
  return { isPremium, isLoading, redirect };
}
